'use strict';

import React, {useState, useMemo, useEffect} from 'react';
import TS from "@zerolight/zlthunder-time-utils";
import DatePicker from "react-datepicker";

import {useFilter, SetFilter} from "./state";
import {apiCall} from "../../shared/api-client";
import config from '../../shared/config';
import {pretty, getFieldName, getFieldTitle} from '../../shared/utils';
import Toggles, {AsteriskTogglesCl} from "../../react-components/toggles";

const DateFormat = 'dd MMM yyyy';

function formatFilterValues(values) {
	if (values[0] === '*') {
		return 'All';
	}
	if (values.length > 3) {
		return `${values.slice(0, 3).join(', ')} and ${values.length - 3} more`;
	}
	return values.join(', ');
}

class TogglesCl extends AsteriskTogglesCl {
	formatValues(getter) {
		return formatFilterValues(Array.from(this.toggledOn(getter)));
	}
}

function FilterSelector({field, options}) {
	const [filter, dispatch] = useFilter();
	return useMemo(() => {
		// This component should not re-render when the filter changes, cause it changes it.
		const togglesCL = new TogglesCl(options);

		const onChange = (toggles) => {
			let value = Array.from(toggles.toggledOn());
			dispatch(SetFilter, field, value);
		};

		let initialValue = filter.get(field);
		initialValue = initialValue && Array.from(initialValue);
		let title = pretty(field);
		let configuredFilters = config.get('Filters', []);
		for (let f of configuredFilters) {
			if (getFieldName(f) === field) {
				title = getFieldTitle(f);
			}
		}

		return (
			<div className="cm-filter">
				<div className="cm-label">{title}:</div>
				<div className="cm-values">
					<Toggles options={togglesCL} initialValue={initialValue} onChange={onChange}/>
				</div>
			</div>
		)
	}, [field, options]);
}

function FilterEditor({presets}) {
	return (
		<div className="cm-filters-hash">
			{
				Object.keys(presets)
					.map(
						k => <FilterSelector key={k} field={k} options={presets[k]}/>
					)
			}
		</div>
	);
}

function useTimeRange(filter, cb, reqs = []) {
	let from = filter.get('from');
	let to = filter.get('to');
	let today = TS.utcDay(new Date());
	from = from ? TS.validate(from) : TS.addUTCDays(today, -29);
	to = to ? TS.validate(to) : TS.addUTCDays(today, 1);

	return useMemo(() => {
		return cb(from, to);
	}, [from.valueOf(), to.valueOf(), ...reqs]);
}

function TimeRangeEditor() {
	const [filter, dispatch] = useFilter();
	return useTimeRange(filter, (from, to) => {
		return <>
			<div className="date-picker">
				<div className="cm-label">From:</div>
				<DatePicker
					selectsStart selected={from}
					startDate={from} endDate={to}
					dateFormat={DateFormat}
					onChange={
						date => dispatch(SetFilter, 'from', date)
					}
				/>
			</div>
			<div className="date-picker">
				<div className="cm-label">To:</div>
				<DatePicker
					selectsEnd selected={to}
					startDate={from} endDate={to} minDate={from}
					dateFormat={DateFormat}
					onChange={
						date => dispatch(SetFilter, 'to', date)
					}
				/>
			</div>
		</>
	});
}

function Filter() {
	const [presets, setPresets] = useState(null);
	useEffect(() => {
		let canceled = false;
		(async () => {
			let response = await apiCall({method: 'stats/presets'});
			if (!canceled) {
				setPresets(response.data);
			}
		})();
		return () => {
			canceled = true;
		}
	}, []);

	if (presets === null) {
		return 'Loading filter values';
	}

	return (
		<>
			<div className="filter">
				<h3>Filter</h3>
				<FilterEditor presets={presets}/>
			</div>
			<div className="time-range">
				<h3>Time range</h3>
				<TimeRangeEditor/>
			</div>
		</>
	);
}

export default Filter;